/**
 * Owner: Masa Bayu
 * File: layout-preprocess.ts
 * Date:Time: 2026-09-28 10:17
 * Description: Deterministic layout → IR · SR-02 typography-gated HEADING/SUBHEADING.
 */

import { createQXDocumentConfidence } from './confidence';
import { createQXDocumentIR, type QXDocumentIR } from './document-ir';
import {
  createQXDocumentBlock,
  createQXDocumentPageMapEntry,
  createQXDocumentWarning,
  type QXDocumentBlock,
  type QXDocumentWarning,
} from './parts';
import type { QXDocumentLayout, QXDocumentLayoutBlock, QXDocumentPage } from './layout';
import {
  buildLayoutStructureSizeModel,
  classifyLayoutTextBlockType,
  mergeConsecutiveHeadingSizedLines,
} from './layout-structure-typing';
import { createQXDocumentProvenance } from './provenance';
import type { QXDocumentSourceType } from './codes';

function lineText(block: QXDocumentLayoutBlock): string {
  if (block.text && block.text.trim().length > 0) return block.text.trim();
  return block.lines
    .map((line) => line.runs.map((run) => run.text).join(''))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Reconstruct hyphenated line breaks: "long-\\nand" → "long and" (proposal text only on block). */
export function reconstructHyphenation(text: string): string {
  return text
    .replace(/(\w)-\s*\n\s*(\w)/g, '$1$2')
    .replace(/(\w)-\s+(\w)/g, '$1$2')
    .replace(/\s*\n\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isLikelyPageNumber(text: string): boolean {
  return /^\d{1,4}$/.test(text.trim());
}

function topBand(page: QXDocumentPage, block: QXDocumentLayoutBlock): boolean {
  if (!block.bbox) return false;
  return block.bbox.y <= page.height * 0.12;
}

function bottomBand(page: QXDocumentPage, block: QXDocumentLayoutBlock): boolean {
  if (!block.bbox) return false;
  return block.bbox.y + block.bbox.height >= page.height * 0.88;
}

function repeatedEdgeTexts(layout: QXDocumentLayout): {
  readonly headers: ReadonlySet<string>;
  readonly footers: ReadonlySet<string>;
} {
  const headerCounts = new Map<string, number>();
  const footerCounts = new Map<string, number>();
  for (const page of layout.pages) {
    for (const block of page.blocks) {
      const text = lineText(block);
      if (text.length === 0 || text.length > 80) continue;
      if (topBand(page, block)) {
        headerCounts.set(text, (headerCounts.get(text) ?? 0) + 1);
      }
      if (bottomBand(page, block) && !isLikelyPageNumber(text)) {
        footerCounts.set(text, (footerCounts.get(text) ?? 0) + 1);
      }
    }
  }
  const minPages = Math.max(2, Math.ceil(layout.pages.length * 0.5));
  const headers = new Set(
    [...headerCounts.entries()].filter(([, n]) => n >= minPages).map(([t]) => t),
  );
  const footers = new Set(
    [...footerCounts.entries()].filter(([, n]) => n >= minPages).map(([t]) => t),
  );
  return { headers, footers };
}

function layoutKindToBlockType(
  kind: QXDocumentLayoutBlock['kind'],
  text: string,
  page: QXDocumentPage,
  block: QXDocumentLayoutBlock,
  edges: { readonly headers: ReadonlySet<string>; readonly footers: ReadonlySet<string> },
  structureModel: ReturnType<typeof buildLayoutStructureSizeModel>,
  prevBlock: QXDocumentLayoutBlock | null,
): QXDocumentBlock['type'] {
  if (kind === 'image') return 'IMAGE';
  if (kind === 'table') return 'TABLE';
  if (kind === 'note') return 'FOOTNOTE';
  if (kind === 'caption') return 'CAPTION';
  if (kind === 'header') return 'HEADER';
  if (kind === 'footer') return 'FOOTER';
  if (kind === 'page_number' || isLikelyPageNumber(text)) return 'PAGE_NUMBER';
  if (edges.headers.has(text) && topBand(page, block)) return 'HEADER';
  if (edges.footers.has(text) && bottomBand(page, block)) return 'FOOTER';
  const structured = classifyLayoutTextBlockType({
    block,
    text,
    model: structureModel,
    page: { height: page.height },
    prevBlock,
  });
  return structured ?? 'PARAGRAPH';
}

/**
 * Eyes preprocess: layout pages → Document IR blocks with provenance/bbox.
 * STRUCTURE-RECOVERY-01: body-relative hierarchy · not document-max HEADING.
 * Does not emit CHAPTER/SECTION semantic decisions.
 */
export function preprocessLayoutToDocumentIR(input: {
  readonly sourceType: QXDocumentSourceType;
  readonly sourceHash: string;
  readonly adapterVersion: string;
  readonly layout: QXDocumentLayout;
  readonly extraWarnings?: readonly QXDocumentWarning[];
}): QXDocumentIR {
  const structureModel = buildLayoutStructureSizeModel(input.layout);
  const layout = mergeConsecutiveHeadingSizedLines({
    layout: input.layout,
    model: structureModel,
  });
  const edges = repeatedEdgeTexts(layout);
  const blocks: QXDocumentBlock[] = [];
  const pageMap: { page: number; blockIds: string[] }[] = [];
  let order = 0;

  for (const page of layout.pages) {
    const ids: string[] = [];
    let prevLayoutBlock: QXDocumentLayoutBlock | null = null;
    for (const layoutBlock of page.blocks) {
      const raw = lineText(layoutBlock);
      if (raw.length === 0 && layoutBlock.kind !== 'image' && layoutBlock.kind !== 'table') {
        continue;
      }
      const text =
        layoutBlock.kind === 'image' || layoutBlock.kind === 'table'
          ? raw.length > 0
            ? raw
            : null
          : reconstructHyphenation(raw);
      const type = layoutKindToBlockType(
        layoutBlock.kind,
        text ?? '',
        page,
        layoutBlock,
        edges,
        structureModel,
        prevLayoutBlock,
      );
      prevLayoutBlock = layoutBlock;
      const bbox = layoutBlock.bbox
        ? Object.freeze([
            layoutBlock.bbox.x,
            layoutBlock.bbox.y,
            layoutBlock.bbox.width,
            layoutBlock.bbox.height,
          ])
        : null;
      const block = createQXDocumentBlock({
        blockId: layoutBlock.layoutBlockId,
        type,
        order,
        text,
        assetId: layoutBlock.assetId,
        provenance: createQXDocumentProvenance({
          sourceType: input.sourceType,
          sourcePage: page.pageNumber,
          sourceObjectId: layoutBlock.layoutBlockId,
          bbox,
          adapterVersion: input.adapterVersion,
        }),
        confidence:
          layoutBlock.confidence ??
          createQXDocumentConfidence({
            target: 'reading-order',
            type: 'deterministic-layout-preprocess',
            confidence: 0.9,
          }),
      });
      blocks.push(block);
      ids.push(block.blockId);
      order += 1;
    }
    pageMap.push({ page: page.pageNumber, blockIds: ids });
  }

  const warnings = [];
  if (edges.headers.size > 0) {
    warnings.push(
      createQXDocumentWarning({
        severity: 'INFO',
        code: 'layout.repeated_header_detected',
        path: 'layout.headers',
      }),
    );
  }
  if (edges.footers.size > 0) {
    warnings.push(
      createQXDocumentWarning({
        severity: 'INFO',
        code: 'layout.repeated_footer_detected',
        path: 'layout.footers',
      }),
    );
  }

  return createQXDocumentIR({
    sourceType: input.sourceType,
    sourceHash: input.sourceHash,
    provenance: createQXDocumentProvenance({
      sourceType: input.sourceType,
      adapterVersion: input.adapterVersion,
    }),
    blocks,
    pageMap: pageMap.map((entry) => createQXDocumentPageMapEntry(entry)),
    layout,
    warnings: [...warnings, ...(input.extraWarnings ?? [])],
  });
}
